
import Vue from "vue";
import Alerter from "@/components/Alerter.vue";
import {
  DevicesService,
  DeviceSchema,
  DeviceHistoriesService,
  OrganizationsService,
} from "@/commons/client";
import { Alert } from "@/commons/types";

export default Vue.extend({
  name: "DeviceList",

  components: {
    Alerter,
  },

  data: () => ({
    searchKey: "",
    alerts: [] as Alert[],
    loading: false,
    organizations: [] as { id: number; name: string }[],
    devices: [] as Array<DeviceSchema & { organizationName?: string | null }>,
    headers: [
      { text: "Seriale", value: "serial_number" },
      { text: "Scheda Madre", value: "motherboard_serial" },
      { text: "IMEI", value: "imei" },
      { text: "Organizzazione", value: "organizationName" },
      { text: "Creato il", value: "created" },
      { text: "Azioni", value: "actions", sortable: false },
    ],
  }),

  computed: {
    filteredDevices(): Array<
      DeviceSchema & { organizationName?: string | null }
    > {
      const key = (this.searchKey || "").toLowerCase();
      return this.devices
        .filter((d) => d.serial_number?.toLowerCase().includes(key))
        .sort((a, b) => a.serial_number.localeCompare(b.serial_number));
    },

    organizationsMap(): Record<number, string> {
      return this.organizations.reduce((acc: any, org: any) => {
        acc[org.id] = org.name;
        return acc;
      }, {});
    },
  },

  methods: {
    async getOrganizations(): Promise<void> {
      try {
        const result =
          await OrganizationsService.organizationListV1OrganizationsGet(
            undefined,
            1000,
            "",
            undefined
          );
        this.organizations = result.map((o) => ({ id: o.id, name: o.name }));
      } catch (error) {
        this.alerts.push({
          type: "error",
          text: "Errore nel recupero delle organizzazioni",
        });
      }
    },

    async getDevices(): Promise<void> {
      this.loading = true;
      try {
        const result = await DevicesService.deviceListV1DevicesGet(
          undefined,
          1000
        );

        this.devices = await Promise.all(
          result.map(async (d) => {
            const formatted: DeviceSchema & {
              organizationName?: string | null;
            } = {
              ...d,
              created: this.formatDateTime(d.created?.toString() || ""),
              organizationName: null,
            };

            if (d.motherboard_serial) {
              try {
                const history =
                  await DeviceHistoriesService.deviceHistoryRetrieveByMoboSerialV1DeviceHistoriesByMoboSerialMoboSerialGet(
                    d.motherboard_serial
                  );

                if (history.organization_id) {
                  formatted.organizationName =
                    this.organizationsMap[history.organization_id] ||
                    `Org ID: ${history.organization_id}`;
                }
              } catch {
                // ignora errori
              }
            }

            return formatted;
          })
        );
      } catch (error) {
        this.alerts.push({
          type: "error",
          text: "Errore nel recupero degli etilometri",
        });
      } finally {
        this.loading = false;
      }
    },

    formatDateTime(dateStr: string): string {
      const date = new Date(dateStr);
      return date.toLocaleString("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },

    async refresh(): Promise<void> {
      this.alerts = [];
      await this.getOrganizations();
      await this.getDevices();
    },
  },

  async mounted() {
    await this.getOrganizations();
    await this.getDevices();
  },
});
